import { useRequirementsStore } from "@/frontend/store/RequirementsStore";
import { useParameterStore } from "@/frontend/store/ParameterStore";
import { useFusaOrganizationStore } from "@/frontend/store/FusaOrganizationStore";
import { I18n } from "@/i18n/I18n";
import { ParameterSubjectType } from "@/types/parameterTypes";
import { RequirementSubjectType } from "@/backend/access/requirement/types";
import { TestMethod } from "@/backend/access/requirementTestMethodAssignment/types";
import { useEffect, useState } from "react";
import { Navigate, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { Dropdown, Placeholder } from "react-bootstrap";
import { ConfirmModal } from "../modals/ConfirmModal";
import { EditRequirementModal } from "./EditRequirementModal";
import { CustomToggle } from "../dropdown/CustomToggle";
import { RequirementFileAttachments } from "./RequirementFileAttachments";
import { RequirementTestMethodAssignments } from "./RequirementTestMethodAssignments";
import { RequirementDocumentationSection, RequirementEvidenceTab } from "./RequirementDocumentationSection";
import { useProjectStore } from "@/frontend/store/ProjectStore";
import { useGlobalStore } from "@/frontend/store/GlobalStore";
import { FusawarePermission } from "@/frontend/fusawareUser";
import { useProcedureListStore } from "@/frontend/store/ProcedureListStore";
import { useWorkspaceStore } from "@/frontend/store/WorkspaceStore";
import { RequirementApplicability } from "./RequirementApplicability";
import { RequirementMilestoneAssignments } from "./RequirementMilestoneAssignments";
import { RequirementSampleMaturityAssignments } from "./RequirementSampleMaturityAssignments";
import { Breadcrumbs, BreadcrumbItem } from "../Breadcrumbs";
import { useProjectLockStore } from "@/frontend/store/ProjectLockStore";
import { ProjectLocked } from "../projects/ProjectLocked";
import { ProjectLayout } from "../projects/ProjectLayout";
import { RequirementCategoryType } from "@/backend/access/requirementEvidenceCategory/types";
import { RequirementProcedureAssignmentsLoader } from "./RequirementProcedureAssignments";
import { RequirementReviewTab } from "./RequirementReviewTab";
import { UserAssignmentSelect } from "../UserAssignmentSelect";
import { VerificationCriteriaTable } from "./VerificationCriteriaTable";
import { RequirementTab, requirementListPath, requirementPath, resolveRequirementTab } from "@/frontend/utils/requirementPath";

function RequirementViewPlaceholder() {
  return (
    <div className="p-4" aria-label={I18n.get('loadingRequirement')}>
      <nav aria-label="breadcrumb">
        <div className="d-flex gap-2">
          <div className="d-flex justify-content-between w-100">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Placeholder animation="wave">
                  <Placeholder xs={4} bg="secondary" />
                </Placeholder>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                <Placeholder animation="wave">
                  <Placeholder xs={6} bg="secondary" />
                </Placeholder>
              </li>
            </ol>
          </div>
        </div>
      </nav>
      <div className="card">
        <div className="card-header">
          <Placeholder as="h3" animation="wave" className="card-title mb-0">
            <Placeholder xs={8} bg="secondary" />
          </Placeholder>
        </div>
        <div className="card-body">
          <div className="d-flex flex-column gap-3">
            <Placeholder animation="wave">
              <Placeholder xs={12} bg="secondary" />
              <Placeholder xs={10} bg="secondary" />
              <Placeholder xs={7} bg="secondary" />
            </Placeholder>
          </div>
        </div>
      </div>
    </div>
  );
}

// Serves both /projects/:projectId/requirements/:requirementId and the older /requirements/:id.
function useRequirementIdParam() {
  const { requirementId, id } = useParams();
  return requirementId ?? id;
}

export function RequirementLoader() {
  const id = useRequirementIdParam();
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const fetchRequirement = useRequirementsStore((state) => state.fetchRequirement);
  const fetchAssignmentsByRequirementId = useRequirementsStore(state => state.fetchAssignmentsByRequirementId);
  const fetchTestMethodAssignmentsByRequirementId = useRequirementsStore(state => state.fetchTestMethodAssignmentsByRequirementId);
  const resetRequirementsStore = useRequirementsStore((state) => state.reset);
  const fetchProject = useProjectStore((state) => state.fetchProject);
  const fetchUsers = useGlobalStore(state => state.fetchUsers);
  const resetProjectStore = useProjectStore((state) => state.reset);
  const getParametersForSubject = useParameterStore((state) => state.getParametersForSubject);
  const resetParameterStore = useParameterStore((state) => state.reset);
  const readPublishedTemplatesForEnterprise = useProcedureListStore(state => state.readPublishedTemplatesForEnterprise);
  const resetProcedureList = useProcedureListStore(state => state.reset);
  const requirement = useRequirementsStore(useShallow((state) => state.requirements.find((r) => r.id === id)));
  const projectLock = useProjectLockStore(useShallow(state => state.projectLocks.find(p => p.projectId === requirement?.subjectId)));
  
  const [loadingRequirements, setLoadingRequirements] = useState(false);
  const [loadingParameters, setLoadingParameters] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingAssignmentsAndProcedures, setLoadingAssignmentsAndProcedures] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  useEffect(() => {
    if (id) {
      setLoadingRequirements(true);
      void fetchRequirement(id).finally(() => {
        setLoadingRequirements(false);
      });
    }

    return () => {
      resetRequirementsStore();
    };
  }, [fetchRequirement, resetRequirementsStore]);

  useEffect(() => {
    if (id) {
      setLoadingParameters(true);
      void getParametersForSubject(id, ParameterSubjectType.REQUIREMENT).finally(() => {
        setLoadingParameters(false);
      });
    }

    return () => {
      resetParameterStore();
    };
  }, [id, getParametersForSubject, resetParameterStore]);

  useEffect(() => {
    if (requirement?.subjectType === RequirementSubjectType.PROJECT && requirement?.subjectId) {
      setLoadingProjects(true);
      void fetchProject(requirement.subjectId).finally(() => {
        setLoadingProjects(false);
      });
    }

    return () => {
      resetProjectStore();
    };
  }, [requirement?.subjectId, fetchProject, resetProjectStore]);

  useEffect(() => {
    if (id) {
      setLoadingAssignmentsAndProcedures(true);
      void fetchAssignmentsByRequirementId(id).finally(() => {
        setLoadingAssignmentsAndProcedures(false);
      });
      void fetchTestMethodAssignmentsByRequirementId(id);
    }

    return () => {
      resetProcedureList();
    };
  }, [id, fetchAssignmentsByRequirementId, fetchTestMethodAssignmentsByRequirementId]);

  useEffect(() => {
    const loadTemplates = async () => {
      if (!id) return;
      setLoadingTemplates(true);
      await Promise.all([
        readPublishedTemplatesForEnterprise(),
      ]);
      setLoadingTemplates(false);
    };

    void loadTemplates();
  }, [readPublishedTemplatesForEnterprise, id]);

  useEffect(() => {
    void fetchUsers();
  }, [fetchUsers]);

  // A project's requirement belongs under its project; send old /requirements/:id links
  // (and a URL naming the wrong project) there, keeping the tab.
  if (!loadingRequirements && requirement?.subjectType === RequirementSubjectType.PROJECT && projectId !== requirement.subjectId) {
    return <Navigate replace to={requirementPath(requirement, resolveRequirementTab(searchParams.get('tab')))} />;
  }

  if (loadingRequirements || loadingParameters || loadingProjects || loadingAssignmentsAndProcedures || loadingTemplates) {
    return <RequirementViewPlaceholder />;
  }

  if(projectLock?.isLocked()) {
    return <ProjectLocked />;
  }

  if (requirement?.subjectType === RequirementSubjectType.PROJECT && requirement.subjectId) {
    return (
      <ProjectLayout projectId={requirement.subjectId} trailingBreadcrumbs={[{ title: requirement.name }]}>
        <RequirementView withinProjectLayout />
      </ProjectLayout>
    );
  }

  return (
    <div>
      <RequirementView />
    </div>
  );
}

function RequirementView(props: { withinProjectLayout?: boolean } = {}) {
  const { withinProjectLayout } = props;
  const id = useRequirementIdParam();
  const [searchParams] = useSearchParams();
  const tab = resolveRequirementTab(searchParams.get('tab'));
  const navigate = useNavigate();
  const requirement = useRequirementsStore(useShallow((state) => state.requirements.find((r) => r.id === id)));
  const testMethodAssignments = useRequirementsStore(useShallow((state) => id ? state.testMethodAssignmentsByRequirement[id] || [] : []));
  const userClass = useGlobalStore(state => state.userClass);
  const updateAssignedSupplierUserId = useRequirementsStore((state) => state.updateAssignedSupplierUserId);
  const deleteRequirement = useRequirementsStore((state) => state.deleteRequirement);

  const testedByDocumentation = testMethodAssignments.some(a => a.testMethod === TestMethod.DOCUMENTATION);
  const testedByProcedure = testMethodAssignments.some(a => a.testMethod === TestMethod.VERIFORM_PROCEDURE);
  
  const fusaOrganization = useFusaOrganizationStore(useShallow((state) => 
    requirement?.subjectType === RequirementSubjectType.FUSA_ORGANIZATION 
      ? state.fusaOrganizations.find(org => org.id === requirement.subjectId)
      : null
  ));
  const project = useProjectStore(useShallow((state) => 
    requirement?.subjectType === RequirementSubjectType.PROJECT 
      ? state.projects.find(project => project.id === requirement.subjectId)
      : null
  ));
  const workspace = useWorkspaceStore(useShallow((state) => state.workspaces.find(workspace => workspace.id === project?.parentId)));
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDelete = async () => {
    if (!id || !requirement) return;
    const listPath = requirementListPath(requirement);
    const success = await deleteRequirement(id);
    if (success) {
      void navigate(listPath);
    }
  };

  const getBreadcrumbLink = () => {
    if (!requirement) return null;
    
    if (requirement.subjectType === RequirementSubjectType.FUSA_ORGANIZATION) {
      return {
        to: `/library/requirements?org=${requirement.subjectId}`,
        label: fusaOrganization?.title ?? I18n.get('fusaOrganization')
      };
    }
    if (requirement.subjectType === RequirementSubjectType.PROJECT) {
      return {
        to: `/projects/${requirement.subjectId}`,
        label: project?.title ?? I18n.get('project')
      };
    }

    return null;
  };

  if (!requirement) {
    return <h6 className="text-muted">{I18n.get('requirementNotFound')}</h6>;
  }

  const breadcrumb = getBreadcrumbLink();
  const isOwner = requirement.propelOrgId === userClass?.getOrgId();
  const visibleTabs: { key: RequirementTab, label: string }[] = [
    { key: 'details', label: I18n.get('details') },
    { key: 'verification', label: I18n.get('verification') },
    { key: 'evidence', label: I18n.get('evidence') },
    ...(isOwner ? [{ key: 'review' as const, label: I18n.get('review') }] : []),
  ];

  const breadcrumbItems: BreadcrumbItem[] = [];
  if (workspace && requirement.subjectType === RequirementSubjectType.PROJECT) {
    breadcrumbItems.push({ title: workspace.title, link: `/workspaces/${workspace.id}` });
  }
  if (breadcrumb) {
    breadcrumbItems.push({ title: breadcrumb.label, link: breadcrumb.to });
  }
  breadcrumbItems.push({ title: requirement.name });

  return (
    <div className="overflow-auto" style={withinProjectLayout ? { height: '100%' } : { maxHeight: '100vh', height: '100vh' }}>
      {!withinProjectLayout && <Breadcrumbs items={breadcrumbItems} />}
      <div className="px-4 pb-4 pt-4">
      <div className="d-flex flex-row justify-content-between">
        <div className='pb-4'>
          <h4 className="card-title mb-0">{requirement.name}</h4>
          {requirement.identifier && (
            <span className="text-muted small mt-1" aria-label={I18n.get('identifier')}>
              {requirement.identifier}
            </span>
          )}
          <p className="mb-0 pt-1">{requirement.description}</p>
        </div>
        {requirement.propelOrgId === userClass?.getOrgId() && (
          <Dropdown drop="down">
            <Dropdown.Toggle size="sm" as={CustomToggle('requirement')} variant="dark" id={`requirement-dropdown-${requirement.id}`} />
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setShowEditModal(true)}>
                {I18n.get('edit')}
              </Dropdown.Item>
              <Dropdown.Item className="text-danger" onClick={() => setShowDeleteModal(true)}>
                {I18n.get('delete')}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>
      <div className='d-print-none d-flex pe-none flex-column'>
        <div className="veriform-tab-bar pe-auto">
          {visibleTabs.map(({ key, label }) => (
            <button
              key={key}
              className={`veriform-tab ${tab === key ? 'active' : ''}`}
              type="button"
              onClick={() => void navigate(requirementPath(requirement, key))}
              aria-label={label}
              title={label}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {tab === 'details' &&
        <div className="d-flex flex-column gap-2">
          <RequirementFileAttachments requirementId={requirement.id} readOnly={!isOwner} />
          <RequirementApplicability requirement={requirement} />
          {requirement.applicable && project && (
            <>
              <RequirementMilestoneAssignments requirementId={requirement.id} projectId={project.id} />
              <RequirementSampleMaturityAssignments requirementId={requirement.id} projectId={project.id} />
            </>
          )}
          {/* The owner's assigned user is set on the Review tab, as the assigned reviewer. */}
          {!isOwner && (
            <div className="mt-2">
              <UserAssignmentSelect
                assignedUserId={requirement.assignedSupplierUserId}
                onAssign={(assignedSupplierUserId) => void updateAssignedSupplierUserId(requirement.id, assignedSupplierUserId)}
              />
            </div>
          )}
        </div>
      }
      {tab === 'verification' &&
        <div className="d-flex flex-column gap-2">
          {requirement.applicable ? (
            <>
              <RequirementTestMethodAssignments requirement={requirement} />

              { testedByProcedure && project && userClass?.fusaHasPermission(FusawarePermission.ACCESS_PROCEDURE_REQUIREMENT_TEST_METHOD) && (
                <RequirementProcedureAssignmentsLoader requirement={requirement} projectId={project.id} />
              )}

              {testedByDocumentation && (
                <RequirementDocumentationSection
                  requirement={requirement}
                  projectId={project?.id}
                  type={RequirementCategoryType.EVIDENCE}
                  mode="categories"
                  showEvidenceTabLink={false}
                />
              )}
            </>
          ) : (
            <p className="text-muted small mt-4 mb-0">{I18n.get('notApplicableNoVerificationMethods')}</p>
          )}
          <RequirementDocumentationSection
            requirement={requirement}
            projectId={project?.id}
            type={RequirementCategoryType.DESIGN}
            mode="categories"
          />
          {/* Criteria are read with the owner's org, so suppliers can't load them yet. */}
          {isOwner && <VerificationCriteriaTable requirementId={requirement.id} />}
        </div>
      }
      {tab === 'evidence' &&
        <div className="d-flex flex-column gap-3">
          <RequirementEvidenceTab
            requirement={requirement}
            projectId={project?.id}
            testedByDocumentation={testedByDocumentation}
          />
        </div>
      }
      {tab === 'review' && isOwner &&
        <RequirementReviewTab requirement={requirement} projectId={project?.id} />
      }

      <EditRequirementModal
        requirement={requirement}
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
      />

      {showDeleteModal && (
        <ConfirmModal
          title={I18n.get('deleteRequirement')}
          confirmMessage={I18n.get('areYouSureDeleteModel', { model: I18n.get('requirementLowercase'), title: requirement.name })}
          onConfirm={() => handleDelete()}
          setShowModal={setShowDeleteModal}
        />
      )}
      </div>
    </div>
  );
}
